import { yupResolver } from '@hookform/resolvers/yup';
import type { FormProps as SystemFormProps } from '@hover/blueprint';
import { Form as SystemForm, system } from '@hover/blueprint';
import React from 'react';
import type {
  DeepPartial,
  Mode,
  SubmitHandler,
  UnpackNestedValue,
} from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import type { ObjectSchema } from 'yup';

interface FormData {
  [key: string]: any;
}

interface FormProps<F extends FormData = FormData>
  extends Omit<SystemFormProps, 'onSubmit'> {
  children: React.ReactNode;
  disabled?: boolean;
  initialValues: UnpackNestedValue<DeepPartial<F>>;
  mode?: Mode;
  onSubmit: SubmitHandler<F>;
  validationSchema: ObjectSchema<FormData>;
}

const Form = <F extends FormData = FormData>({
  children,
  disabled,
  initialValues,
  method = 'post',
  mode,
  onSubmit,
  width: widthProp,
  w: wProp,
  validationSchema,
  ...props
}: FormProps<F>) => {
  const width = widthProp ?? wProp;

  const formProps = useForm<F>({
    defaultValues: initialValues,
    mode,
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit } = formProps;
  const onSubmitProxy = handleSubmit(onSubmit);

  return (
    <FormProvider {...formProps}>
      <system.fieldset
        border="none"
        disabled={disabled}
        margin={0}
        padding={0}
        width={width}
      >
        <SystemForm
          display="grid"
          method={method}
          {...props}
          noValidate
          onSubmit={onSubmitProxy}
          width={width}
        >
          {children}
        </SystemForm>
      </system.fieldset>
    </FormProvider>
  );
};

export { Form };
